import React, { useState, useEffect } from 'react';
import {
    Box,
    Button,
    TextField,
    CircularProgress,
    Card,
    CardContent,
    CardActions,
    Typography,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid2,
    Avatar,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment
} from '@mui/material';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';

const API_URL = process.env.REACT_APP_API_ROOT || 'http://localhost:4000';

function Agents() {
    const [agents, setAgents] = useState([]);
    const [expressions, setExpressions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAgent, setSelectedAgent] = useState(null);
    const [agentDetails, setAgentDetails] = useState({
        name: '',
        role: '',
        backstory: '',
        goal: '',
        avatar: '',
        expression: '',
        rawText: '',
        definitionMethod: 'crewAI',
    });
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [agentToDelete, setAgentToDelete] = useState(null);
    const [agentDialogOpen, setAgentDialogOpen] = useState(false);
    const [avatarLoading, setAvatarLoading] = useState(false);
    const { user, getAccessTokenSilently } = useAuth0();

    // refresh agent list when agent is updated, created or deleted


    // fetch all expressions once
    useEffect(() => {
        (async () => {
            const token = await getAccessTokenSilently();
            const res = await axios.get(`${API_URL}/api/simulations/expression`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setExpressions(res.data);
            console.log('fetched expressions:', res.data);
        })();
    }, [getAccessTokenSilently]);


    useEffect(() => {
        console.log('agentDetails.expression:', agentDetails.expression);
        if (typeof(agentDetails.expression) === 'string' && agentDetails.expression.length > 0) {
            const selected = expressions.find(e => e._id === agentDetails.expression);
            console.log('selected expression:', selected);
            setAgentDetails(prev => ({
                ...prev,
                expression: selected
            }));
        }
    }, [agentDetails.expression, expressions]);

    useEffect(() => {
        axios
            .get(`${API_URL}/api/agents`)
            .then((response) => {
                setAgents(response.data);
                setLoading(false);
            })
            .catch((error) => {
                console.error('Error fetching agents:', error);
                setLoading(false);
            });
    }, [selectedAgent, agentDialogOpen]);

    // build prompt based on definitionMethod
    const generateAvatarFromAvailableData = async (agent) => {

        console.log('agent:', agent);
        let prompt = '';
        if (agent.definitionMethod === 'crewAI') {
            const { name, role, backstory, goal } = agent;
            prompt = `Create an artistic render (without any text) for an avatar profile image, which depicts someone who's personality could be described by the following info: \n\n ${name} who is a ${role} with the following backstory: ${backstory}. Their goal is: ${goal}.`;
        } else if (agent.definitionMethod === 'expression') {
            prompt = `Create an artistic render (without any text) for an avatar profile image, which depicts someone who's personality could be described by the following info: \n\n ${agent.name} using expression: ${agent.expression ? agent.expression.expression.map(op => op.name).join(' and ') : "none"}.`;
        } else if (agent.definitionMethod === 'rawText') {
            prompt = `Create an artistic render (without any text) for an avatar profile image, which depicts someone who's personality could be described by the following info: \n\n ${agent.name}. "${agent.rawText}"`;
        }   
        console.log('generating avatar with prompt:', prompt);
        // send prompt to HF endpoint without credentials
        const openAISK = process.env.REACT_APP_OPENAI_API_KEY;
        if (!openAISK) console.error('Missing REACT_APP_OPENAI_API_KEY');
        const response = await axios.post(
            'https://api.openai.com/v1/images/generations',
            { model: "dall-e-2", prompt: prompt, size: "256x256", response_format: "b64_json" },
            {
                headers: {
                    Authorization: `Bearer ${openAISK}`
                },
                withCredentials: false
            }
        );
        // convert arraybuffer to base64-encoded PNG data URI
        const base64String = response.data.data[0].b64_json;
        return `data:image/png;base64,${base64String}`;
    };

    const handleAgentSelect = (agent) => {
        setSelectedAgent(agent);
        const method = agent.definitionMethod || (agent.expression ? 'expression' : 'crewAI');
        setAgentDetails({
            name: agent.name,
            role: method === 'crewAI' ? agent.crew_settings?.role || '' : '',
            backstory: method === 'crewAI' ? agent.crew_settings?.backstory || '' : '',
            goal: method === 'crewAI' ? agent.crew_settings?.goal || '' : '',
            avatar: agent.avatar,
            expression: method === 'expression' ? agent.expression || '' : '',
            rawText: method === 'rawText' ? agent.rawText || '' : '',
            definitionMethod: method
        });
        setAgentDialogOpen(true);
    };

    const handleAgentDetailsChange = (event) => {
        const { name, value } = event.target;
        setAgentDetails((prevDetails) => ({
            ...prevDetails,
            [name]: value,
        }));
    };

    const handleSaveAgent = async () => {
        const { definitionMethod, name, role, backstory, goal, avatar, expression, rawText } = agentDetails;
        const agentID = selectedAgent?.id || 'new';
        const token = await getAccessTokenSilently();

        // base payload
        const base = { id: agentID, name, avatar, definitionMethod };
        // branch on definitionMethod
        let body;
        if (definitionMethod === 'crewAI') {
            body = { ...base, crew_settings: { role, backstory, goal } };
        }
        else if (definitionMethod === 'expression') {
            body = { ...base, expression };
        }
        else /* rawText */ {
            body = { ...base, rawText };
        }

        await axios.post(`${API_URL}/api/agents/${agentID}`, body, {
            headers: { Authorization: `Bearer ${token}` }
        })
        .catch((error) => console.error('Error saving agent:', error));

        setAgentDialogOpen(false);
        setSelectedAgent(null);
        setAgentDetails({
            name: '', role: '', backstory: '', goal: '', avatar: '',
            expression: '', rawText: '', definitionMethod: 'crewAI'
        });
    };

    const handleNewAgent = () => {
        setSelectedAgent(null);
        setAgentDetails({
            name: '', role: '', backstory: '', goal: '',
            avatar: '', expression: '', rawText: '',
            definitionMethod: 'crewAI'
        });
        setAgentDialogOpen(true);
    };

    const handleDeleteAgent = (agent) => {
        setAgentToDelete(agent);
        setDeleteDialogOpen(true);
    };

    const confirmDeleteAgent = () => {
        const token = getAccessTokenSilently();
        axios
            .delete(`${API_URL}/api/agents/${agentToDelete.id}`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                }
            })
            .then(() => {
                setAgents((prevAgents) => prevAgents.filter((agent) => agent.id !== agentToDelete.id));
                setDeleteDialogOpen(false);
                setAgentToDelete(null);
                setAgentDetails({
                    name: '',
                    role: '',
                    backstory: '',
                    goal: '',
                    avatar: '',
                });
                setSelectedAgent(null);
            })
            .catch((error) => {
                console.error('Error deleting agent:', error);
            });
    };

    return (
        <>
            <Box mt={2} display="flex" flexDirection="column">
                {loading ? (
                    <CircularProgress />
                ) : (
                    <>
                        <Box mb={2}>
                            <Button variant="contained" color="primary" onClick={handleNewAgent}>
                                New Agent
                            </Button>
                        </Box>

                        {/* YOUR AGENTS SECTION */}
                        <Typography variant="h6">Your Agents</Typography>
                        <Grid2 container spacing={2} sx={{ flexWrap: 'wrap', mb: 2 }}>
                            {agents.filter(a => a.ownerId === user.sub).map(agent => (
                                <Grid2 item key={agent.id} sx={{ flex: '0 0 25%', minWidth: '25%' }}>
                                    <Card onClick={() => handleAgentSelect(agent)} sx={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
                                        <CardContent>
                                            <Avatar src={agent.avatar} sx={{ width: 60, height: 60, mb: 2 }} />
                                            <Typography variant="h5">{agent.name}</Typography>
                                            <Typography variant="subtitle1">{agent.definitionMethod}</Typography>
                                        </CardContent>
                                        <CardActions>
                                            <Button size="small" color="secondary" onClick={e => { e.stopPropagation(); handleDeleteAgent(agent); }}>Delete</Button>
                                        </CardActions>
                                    </Card>
                                </Grid2>
                            ))}
                        </Grid2>

                        {/* ALL AGENTS SECTION */}
                        <Typography variant="h6">All Agents</Typography>
                        <Grid2 container spacing={2} sx={{ flexWrap: 'wrap' }}>
                            {agents.map(agent => (
                                <Grid2
                                    item
                                    key={agent.id}
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'stretch',
                                        flex: '0 0 25%',
                                        minWidth: '25%'
                                    }}
                                >
                                    <Card
                                        onClick={() => handleAgentSelect(agent)}
                                        sx={{
                                            cursor: 'pointer',
                                            flex: 1,
                                            width: '100%',
                                            display: 'flex',
                                            flexDirection: 'column',
                                            justifyContent: 'space-between'
                                        }}
                                    >
                                        <CardContent>
                                            <Avatar
                                                src={agent.avatar}
                                                alt={agent.name}
                                                sx={{ width: 60, height: 60, mb: 2 }}
                                            />
                                            <Typography variant="h5">
                                                {agent.name}
                                            </Typography>
                                            <Typography variant="subtitle1">
                                                {agent.definitionMethod}
                                            </Typography>
                                            <Typography variant="body2" sx={{ fontStyle: 'italic' }}>
                                                {agent.id}
                                            </Typography>
                                        </CardContent>
                                        <CardActions>
                                            <Button
                                                size="small"
                                                color="secondary"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleDeleteAgent(agent);
                                                }}
                                            >
                                                Delete
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Grid2>
                            ))}
                        </Grid2>
                    </>
                )}
            </Box>

            {/* AGENT CREATE / EDIT DIALOG */}
            <Dialog open={agentDialogOpen} onClose={() => setAgentDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>{selectedAgent ? 'Edit Agent' : 'New Agent'}</DialogTitle>
                <DialogContent dividers>
                    <Box display="flex">
                        {/* Avatar preview / placeholder */}
                        <Box flex="0 0 120px" display="flex" flexDirection="column" alignItems="center" mr={2}>
                            <Avatar
                                src={agentDetails.avatar || ''}
                                sx={{ width: 100, height: 100, mb: 1 }}
                            >
                                {!agentDetails.avatar && agentDetails.name.charAt(0)}
                            </Avatar>
                        </Box>

                        {/* Form fields */}
                        <Box flex="1">
                            {/* Definition Method selector */}
                            <FormControl fullWidth margin="normal">
                                <InputLabel id="method-label">Definition Method</InputLabel>
                                <Select
                                    labelId="method-label"
                                    name="definitionMethod"
                                    value={agentDetails.definitionMethod}
                                    onChange={handleAgentDetailsChange}
                                    label="Definition Method"
                                >
                                    <MenuItem value="crewAI">crewAI template</MenuItem>
                                    <MenuItem value="expression">by expression</MenuItem>
                                    <MenuItem value="rawText">raw text</MenuItem>
                                </Select>
                            </FormControl>

                            {/* Name */}
                            <TextField
                                label="Agent Name"
                                name="name"
                                value={agentDetails.name}
                                onChange={handleAgentDetailsChange}
                                margin="normal"
                                fullWidth
                            />

                            {/* Avatar URL with generate button */}
                            <TextField
                                label="Avatar URL"
                                name="avatar"
                                value={agentDetails.avatar.startsWith('data:image/png;base64,') ? 'png image' : agentDetails.avatar}
                                onChange={handleAgentDetailsChange}
                                margin="normal"
                                fullWidth
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {avatarLoading ? (
                                                <CircularProgress size={20} />
                                            ) : (
                                                <Button
                                                    size="small"
                                                    onClick={async () => {
                                                        setAvatarLoading(true);
                                                        const avatar = await generateAvatarFromAvailableData(agentDetails);
                                                        console.log('generated avatar:', avatar);
                                                        setAgentDetails(prev => ({ ...prev, avatar }));
                                                        setAvatarLoading(false);
                                                    }}
                                                >
                                                    Generate
                                                </Button>
                                            )}
                                        </InputAdornment>
                                    )
                                }}
                            />

                            {/* crewAI template fields */}
                            {agentDetails.definitionMethod === 'crewAI' && (
                                <>
                                    <TextField label="Agent Role" name="role" value={agentDetails.role}
                                        onChange={handleAgentDetailsChange} margin="normal" fullWidth />
                                    <TextField multiline rows={6} label="Agent Backstory" name="backstory"
                                        value={agentDetails.backstory} onChange={handleAgentDetailsChange}
                                        margin="normal" fullWidth />
                                    <TextField multiline rows={2} label="Agent Goal" name="goal"
                                        value={agentDetails.goal} onChange={handleAgentDetailsChange}
                                        margin="normal" fullWidth />
                                </>
                            )}

                            {/* by expression dropdown */}
                            {agentDetails.definitionMethod === 'expression' && (
                                <FormControl fullWidth margin="normal">
                                    <InputLabel id="expr-label">Expression</InputLabel>
                                    <Select
                                        labelId="expr-label"
                                        name="expression"
                                        value={typeof(agentDetails.expression) === 'string' ? agentDetails.expression : agentDetails.expression._id}
                                        onChange={handleAgentDetailsChange}
                                        label="Expression"
                                    >
                                        <MenuItem value="">
                                            <em>None</em>
                                        </MenuItem>
                                        {expressions.map(expr => {
                                            // Format date_created as "DD/MM/YY HH:MM"
                                            let formattedDate = '';
                                            if (expr.date_created) {
                                                const d = new Date(expr.date_created);
                                                const pad = n => n.toString().padStart(2, '0');
                                                formattedDate = `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear().toString().slice(-2)} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
                                            }
                                            return (
                                                <MenuItem key={expr._id} value={expr._id}>
                                                    {expr.expression.map(op => op.id).join(' + ')} ({formattedDate})
                                                </MenuItem>
                                            );
                                        })}
                                    </Select>
                                </FormControl>
                            )}
                            {agentDetails.definitionMethod === 'rawText' && (
                                <TextField multiline rows={8} label="Raw Prompt" name="rawText"
                                    value={agentDetails.rawText} onChange={handleAgentDetailsChange}
                                    margin="normal" fullWidth />
                            )}
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleSaveAgent} color="primary">Save</Button>
                    <Button onClick={() => setAgentDialogOpen(false)} color="secondary">Cancel</Button>
                </DialogActions>
            </Dialog>

            {/* DELETE CONFIRMATION DIALOG */}
            <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <Typography>Are you sure you want to delete this agent?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} color="secondary">
                        Cancel
                    </Button>
                    <Button onClick={confirmDeleteAgent} color="primary">
                        Confirm
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}

export default Agents;